import { useDateFormatter, useTranslator } from '@bluebeam/react-i18n';
import { useAuth } from 'auth';
import Calendar from 'components/Icons/Calendar';
import Label from 'components/inputs/Label';
import SubmitallViewerModal from 'components/modals/SubmittalViewerModal';
import OverlayFooter from 'components/Overlays/OverlayFooter';
import OverlayHeader from 'components/Overlays/OverlayHeader';
import OverlaySection from 'components/Overlays/OverlaySection';
import OverlayStdContainer from 'components/Overlays/OverlayStdContainer';
import profile24 from 'images/icons/profile-24.svg';
import Immutable from 'immutable';
import React, { useEffect } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Doc } from 'records/Doc';
import { AssignSubmittal } from 'records/interactions/AssignSubmittal';
import { EditSubmittal } from 'records/interactions/EditSubmittal';
import { RespondToSubmittal } from 'records/interactions/RespondToSubmittal';
import { ReviewSubmittal } from 'records/interactions/ReviewSubmittal';
import { ViewSubmittal } from 'records/interactions/ViewSubmittal';
import { ModalState } from 'records/ModalState';
import { clearQueryParam } from 'utils/url';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import DeleteSubmittalButton from '../DeleteSubmittalButton';
import SubmittalDocTile from '../SubmittalDocTile';

interface Props extends WithAppStoreProps, RouteComponentProps {
  interaction: ViewSubmittal;
}

const ViewSubmittalOverlay = (props: Props) => {
  const { userId } = useAuth();
  const { translate } = useTranslator([
    'statusLabels',
    'buttons',
    'submittalsPage',
  ]);
  const dateFormatter = useDateFormatter({
    dateStyle: 'long',
  });

  useEffect(() => {
    init();
  }, []);

  const init = async (): Promise<void> => {
    const { interaction, actions } = props;
    const { submittal } = interaction;

    if (submittal.attachments.size > 0) {
      actions.getDocuments(submittal.projectid, submittal.attachments);
      await Promise.all(
        submittal.attachments.map((docId) => actions.getPages(docId)).toArray()
      );
    }
  };

  const getSubmittalDocs = (): Immutable.List<Doc> => {
    const { interaction, appState } = props;
    const { submittal } = interaction;
    const { documents } = appState;

    const attachmentSet = submittal.attachments.toSet();

    const submittalDocs = documents
      .filter((d) => attachmentSet.has(d.documentid))
      .valueSeq()
      .toList();

    return submittalDocs;
  };

  const handleCancelClick = (): void => {
    const { actions, history } = props;
    actions.clearInteractions();
    clearQueryParam('submittal', history);
  };

  const handleEditSubmittal = async (): Promise<void> => {
    const { interaction, actions } = props;

    const editSubmittalInteraction = new EditSubmittal({
      submittal: interaction.submittal,
    });

    await actions.clearInteractions();
    actions.createInteraction(editSubmittalInteraction);
  };

  const handleRespondToSubmittal = async (): Promise<void> => {
    const { interaction, actions } = props;
    const { submittal } = interaction;

    const subRequested = submittal.status.kind === 'SUB_SUBMITTAL_REQUESTED';
    const subDesignReview =
      submittal.status.kind === 'SUB_SUBMITTED_FOR_DESIGN_REVIEW';

    if (subRequested) {
      const editSubmittalInteraction = new RespondToSubmittal({
        submittal: interaction.submittal,
      });

      await actions.clearInteractions();
      actions.createInteraction(editSubmittalInteraction);
    }

    if (subDesignReview) {
      const editSubmittalInteraction = new ReviewSubmittal({
        submittal: interaction.submittal,
      });

      await actions.clearInteractions();
      actions.createInteraction(editSubmittalInteraction);
    }
  };

  const handleAssignSubmittal = async (): Promise<void> => {
    const { actions, interaction } = props;
    const { submittal } = interaction;

    const assignSubmittalInteraction = new AssignSubmittal({
      submittal,
    });

    await actions.deleteInteractions('ViewSubmittal');
    await actions.createInteraction(assignSubmittalInteraction);
  };

  const handleReviewSubmittal = (): void => {
    const { interaction, actions } = props;
    const { submittal } = interaction;

    const submittalDocs = getSubmittalDocs();

    const modalState = new ModalState({
      id: 'SubmittalViewer',
      portal: 'main',
      open: true,
      shouldCloseOnOverlayClick: false,
      shouldCloseOnEsc: false,
      component: SubmitallViewerModal,
      props: {
        doc: submittalDocs.first(),
        submittalId: submittal.id,
        showToolbar: true,
      },
    });

    actions.createModal(modalState);
    actions.clearInteractions();
  };

  const getDueDate = (): string => {
    const { interaction } = props;
    const { submittal } = interaction;
    const subRequested = submittal.status.kind === 'SUB_SUBMITTAL_REQUESTED';
    const subForDesignReview =
      submittal.status.kind === 'SUB_SUBMITTED_FOR_DESIGN_REVIEW';

    if (subRequested && submittal.duefromsub > 0) {
      const duedate = new Date(submittal.duefromsub * 1000);
      const localeOptions = { year: 'numeric', month: 'short', day: 'numeric' };

      return duedate.toLocaleDateString(
        'en-US',
        localeOptions as Intl.DateTimeFormatOptions
      );
    }

    if (subForDesignReview && submittal.duefromreviewer > 0) {
      const duedate = new Date(submittal.duefromreviewer * 1000);
      const localeOptions = { year: 'numeric', month: 'short', day: 'numeric' };

      return duedate.toLocaleDateString(
        'en-US',
        localeOptions as Intl.DateTimeFormatOptions
      );
    }

    return '';
  };

  const getConfirmText = (): string => {
    const {
      interaction: { submittal },
    } = props;

    let confirmText = translate('buttons:respond');

    switch (submittal.status.kind) {
      case 'SUB_NOT_REVIEWED':
        confirmText = translate('buttons:assign');
        break;
      case 'SUB_NOT_YET_REQUESTED':
        confirmText = translate('buttons:assign');
        break;
      case 'SUB_SUBMITTAL_REQUESTED':
        confirmText = translate('buttons:respond');
        break;
      case 'SUB_SUBMITTED_FOR_DESIGN_REVIEW':
        confirmText = translate('buttons:respond');
        break;
      case 'SUB_PENDING_GC_APPROVAL':
        confirmText = translate('buttons:review');
        break;
      default:
    }

    return confirmText;
  };

  const handleConfirmClick = async (): Promise<void> => {
    const { interaction } = props;
    const { submittal } = interaction;

    switch (submittal.status.kind) {
      case 'SUB_SUBMITTAL_REQUESTED':
        handleRespondToSubmittal();
        break;
      case 'SUB_PENDING_GC_APPROVAL':
        handleReviewSubmittal();
        break;
      case 'SUB_NOT_YET_REQUESTED':
        handleAssignSubmittal();
        break;
      case 'SUB_SUBMITTED_FOR_DESIGN_REVIEW':
        handleReviewSubmittal();
        break;
      default:
    }
  };

  const { interaction, appState } = props;
  const { users, projects, selectedProject } = appState;
  const { submittal } = interaction;

  const project = projects.get(selectedProject);

  const currentUser = users.find((u) => userId === u.id);
  const userEmail = currentUser?.email.toLowerCase();

  // "If the status is "Submittal Requested" and you are either assigned to the submittal, or and admin, you can respond to it."
  const subRequested = submittal.status.kind === 'SUB_SUBMITTAL_REQUESTED';
  const subDesignReview =
    submittal.status.kind === 'SUB_SUBMITTED_FOR_DESIGN_REVIEW';
  const approved = submittal.status.kind === 'SUB_APPROVED';
  const rejected = submittal.status.kind === 'SUB_REJECTED';
  const resubmit = submittal.status.kind === 'SUB_RESUBMIT_REQUIRED';

  const hasResponse = approved || rejected || resubmit;

  const hasDueDate = getDueDate() !== '';

  const canRespond =
    project?.permissions.activeProject &&
    (subRequested || subDesignReview) &&
    (submittal.ballincourt.toLowerCase() === userEmail ||
      project?.permissions.managePeople) &&
    !hasResponse;

  const isAdmin =
    project?.permissions.activeProject && project?.permissions.adminSubmittal;

  const canEdit =
    project?.permissions.activeProject &&
    project?.permissions.adminSubmittal &&
    !hasResponse;

  const submittalDocs = getSubmittalDocs();

  const assignee =
    submittal.ballincourt === ''
      ? translate('submittalsPage:unassigned')
      : submittal.ballincourt;

  const reviewerResponse: `${string}:${string}` =
    `submittalsPage:reviewerResponse.${submittal.reviewerresponse.kind}` as `${string}:${string}`;

  const categoryType: `${string}:${string}` =
    `submittalsPage:categories.${submittal.category.kind}` as `${string}:${string}`;

  const statusType: `${string}:${string}` =
    `statusLabels:submittals.${submittal.status.kind}` as `${string}:${string}`;

  return (
    <OverlayStdContainer
      id="view-sub-overlay"
      style={{ backgroundColor: 'var(--white-2)' }}
    >
      <OverlayHeader
        id="view-sub-overlay-header"
        title={`${translate('submittalsPage:submittalNumber')}: ${
          submittal.specsectionnumber
        }-${submittal.sectioncount.toString().padStart(3, '0')}`}
        onCancelClick={handleCancelClick}
      />
      <div className="column justify-between oa grow-1">
        <div className="mx4 my2">
          <OverlaySection label={translate('submittalsPage:progress')}>
            <div className="black-1 font-size-7 medium mb4">
              {hasResponse
                ? `${translate(statusType)} - ${translate(reviewerResponse)}`
                : translate(statusType)}
            </div>
            <div className="row justify-between items-center">
              <div className="row items-center">
                <img className="mr1" src={profile24} alt="profile icon" />
                <div>{assignee}</div>
              </div>
              <div>
                {hasDueDate ? (
                  <>
                    <Calendar width="14px" height="14px" />
                    <span className="ml2">
                      {dateFormatter.format(new Date(getDueDate()))}
                    </span>
                  </>
                ) : null}
              </div>
            </div>
            {submittalDocs.size > 0 ? (
              <div className="mt4">
                <div
                  className="font-size-5 mb2"
                  style={{ color: 'var(--black-2)' }}
                >
                  {translate('submittalsPage:attachment')}
                </div>
                {submittalDocs
                  .map((sd) => (
                    <SubmittalDocTile
                      key={sd.documentid}
                      submittal={submittal}
                      doc={sd}
                    />
                  ))
                  .toArray()}
              </div>
            ) : null}
          </OverlaySection>
          {/* <OverlayBreak /> */}
          <OverlaySection label={translate('submittalsPage:generalDetails')}>
            <div className="mb3">
              <Label
                label={`${translate('submittalsPage:submittalNumberFull')}:`}
              />
              {submittal.specsectionnumber}
            </div>
            <div className="mb3">
              <Label label={`${translate('submittalsPage:specSection')}:`} />
              {submittal.specsectionname}
            </div>
            <div className="mb3">
              <Label
                label={`${translate('submittalsPage:submittalCategory')}:`}
              />
              {translate(categoryType)}
            </div>
            <div className="mb3">
              <Label label={`${translate('submittalsPage:description')}:`} />
              {submittal.description}
            </div>
            <div className="mb3">
              <Label
                label={`${translate('submittalsPage:responsibleCompany')}:`}
              />
              {submittal.responsiblecompany}
            </div>
            {isAdmin ? (
              <>
                <div className="row hs3">
                  <div className="w50 mr3">
                    <Label label={`${translate('submittalsPage:leadTime')}:`} />
                    {submittal.leadtime}
                  </div>
                  <div className="w50">
                    <Label
                      label={`${translate('submittalsPage:requiredOnSite')}:`}
                    />
                    {submittal.requiredonsite
                      ? dateFormatter.format(
                          new Date(submittal.requiredonsite * 1000)
                        )
                      : ''}
                  </div>
                </div>
              </>
            ) : null}
          </OverlaySection>
          <OverlaySection label={translate('submittalsPage:submittalRequest')}>
            <div className="row mb3">
              <div className="w50 mr3">
                <Label
                  label={`${translate('submittalsPage:requestedDate')}:`}
                />
                {/* should be blank if subassignedby is null */}
                {submittal.subassignedon
                  ? dateFormatter.format(
                      new Date(submittal.subassignedon * 1000)
                    )
                  : ''}
              </div>
              <div className="w50">
                <Label label={`${translate('submittalsPage:dueSubmitter')}:`} />
                {submittal.duefromsub
                  ? dateFormatter.format(new Date(submittal.duefromsub * 1000))
                  : ''}
              </div>
            </div>
            <div className="row hs3">
              <div className="w50 mr3 truncate">
                <Label label={`${translate('submittalsPage:receivedFrom')}:`} />
                {submittal.subrespondedby}
              </div>
              <div className="w50">
                <Label label={`${translate('submittalsPage:receivedOn')}:`} />
                {submittal.subrespondedby
                  ? dateFormatter.format(
                      new Date(submittal.subrespondedon * 1000)
                    )
                  : ''}
              </div>
            </div>
          </OverlaySection>
          <OverlaySection label={translate('submittalsPage:designRequest')}>
            <div className="row mb3">
              <div className="w50 mr3">
                <Label
                  label={`${translate('submittalsPage:requestedDate')}:`}
                />
                {/* unsure */}
                {submittal.reviewerassignedon
                  ? dateFormatter.format(
                      new Date(submittal.reviewerassignedon * 1000)
                    )
                  : ''}
              </div>
              <div className="w50">
                <Label label={`${translate('submittalsPage:dueReviewer')}:`} />
                {submittal.duefromreviewer
                  ? dateFormatter.format(
                      new Date(submittal.duefromreviewer * 1000)
                    )
                  : ''}
              </div>
            </div>
            <div className="row hs3 mb3">
              <div className="w50 mr3 truncate">
                <Label label={`${translate('submittalsPage:receivedFrom')}:`} />
                {submittal.reviewedby}
              </div>
              <div className="w50">
                <Label label={`${translate('submittalsPage:receivedOn')}:`} />
                {submittal.reviewedby
                  ? dateFormatter.format(new Date(submittal.reviewedon * 1000))
                  : ''}
              </div>
            </div>
            <div className="row">
              <div className="w50 mr3">
                <Label
                  label={`${translate(
                    'submittalsPage:reviewerResponseTitle'
                  )}:`}
                />
                {submittal.reviewerresponse
                  ? translate(reviewerResponse)
                  : null}
              </div>
            </div>
          </OverlaySection>
          {isAdmin ? (
            <div className="my3">
              <DeleteSubmittalButton submittal={submittal} />
            </div>
          ) : null}
        </div>
      </div>
      {canRespond || canEdit ? (
        <OverlayFooter
          id="view-sub-overlay-footer"
          confirmText={getConfirmText()}
          onConfirmClick={handleConfirmClick}
          confirmDisabled={false}
          hideCancel
        >
          {canEdit ? (
            <button
              id="view-sub-overlay-footer-edit-btn"
              className="btn btn-outline-primary"
              onClick={handleEditSubmittal}
            >
              {translate('buttons:edit')}
            </button>
          ) : null}
        </OverlayFooter>
      ) : null}
    </OverlayStdContainer>
  );
};

export default withRouter(withAppStore<Props>(ViewSubmittalOverlay));
